import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../components/auth/AuthContext';
import { useOFEFetch } from './useOFE';

export default function OFEFinancialTracker() {
  const { id: project } = useParams();
  const { user } = useAuth();
  const [tab, setTab] = useState('pos');
  const projectQuery = project ? `?project=${project}` : '';
  const { data: summary } = useOFEFetch(project ? `/ofe/financials/projects/${project}` : null, user, [project]);
  const { data: pos } = useOFEFetch(`/ofe/financials/pos${projectQuery}`, user, [project]);
  const { data: jes } = useOFEFetch('/ofe/financials/je?status=proposed', user);
  const { data: exposures } = useOFEFetch(`/ofe/financials/exposure${projectQuery}`, user, [project]);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-4">
        {project && <Link to={`/ofe/projects/${project}`} className="text-gray-400 hover:text-white text-sm">← {project}</Link>}
        <h1 className="text-xl font-bold text-white">Financial Tracker {project && <span className="text-blue-400 font-mono text-lg">— {project}</span>}</h1>
        <Link to={project ? `/ofe/projects/${project}/financials/je-review` : '/ofe/financials/je'} className="ml-auto bg-purple-700 hover:bg-purple-600 text-white px-3 py-1.5 rounded text-sm">
          JE Review Queue {jes?.length > 0 && <span className="bg-red-500 text-white text-xs rounded-full px-1.5 ml-1">{jes.length}</span>}
        </Link>
      </div>

      {summary && (
        <div className="grid grid-cols-4 gap-3">
          {[
            { label: 'Total PO Value', value: `$${(summary.total_po_value||0).toLocaleString()}`, color: 'text-white' },
            { label: 'Invoiced', value: `$${(summary.total_invoiced||0).toLocaleString()}`, color: 'text-green-400' },
            { label: 'Open Exposure', value: `$${(summary.open_exposure||0).toLocaleString()}`, color: 'text-yellow-400' },
            { label: 'Pending JEs', value: summary.pending_je_count, color: 'text-purple-400' },
          ].map(m => (
            <div key={m.label} className="bg-gray-800 border border-gray-700 rounded p-3">
              <div className={`text-xl font-bold ${m.color}`}>{m.value}</div>
              <div className="text-gray-400 text-xs mt-1">{m.label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        {['pos', 'je', 'exposure'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-3 py-1.5 text-sm rounded uppercase tracking-wide ${tab === t ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
            {t === 'pos' ? 'Oracle POs' : t === 'je' ? 'JE Proposals' : 'Exposure Items'}
          </button>
        ))}
      </div>

      {tab === 'pos' && (
        <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-900 text-gray-400">
              <tr><th className="text-left px-4 py-3">PO Number</th><th className="text-left px-4 py-3">Project</th><th className="text-right px-4 py-3">Total</th><th className="text-left px-4 py-3">Status</th></tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {(pos || []).map(po => (
                <tr key={po.id} className="hover:bg-gray-750">
                  <td className="px-4 py-3 font-mono text-blue-400">{po.po_number}</td>
                  <td className="px-4 py-3 text-gray-300 font-mono text-xs">{po.project_allocation || '—'}</td>
                  <td className="px-4 py-3 text-right text-white">${po.total_amount?.toLocaleString()}</td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded-full bg-gray-700 text-gray-300">{po.status}</span></td>
                </tr>
              ))}
              {!pos?.length && <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500">No POs</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'je' && (
        <div className="space-y-3">
          {(jes || []).map(je => (
            <div key={je.id} className="bg-gray-800 border border-yellow-800/40 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <span className="text-yellow-400 font-semibold text-sm">JE Proposal — {je.trigger_type}</span>
                <span className="text-gray-400 text-xs">{je.created_at?.slice(0, 10)}</span>
              </div>
              <div className="text-gray-300 text-sm mt-1">{je.notes}</div>
              <Link to={`/ofe/projects/${project || 'all'}/financials/je-review`} className="text-blue-400 text-xs hover:text-blue-300 mt-2 inline-block">Review →</Link>
            </div>
          ))}
          {!jes?.length && <div className="text-gray-500 text-center py-8">No pending JE proposals</div>}
        </div>
      )}

      {tab === 'exposure' && (
        <div className="space-y-3">
          {(exposures || []).map(e => (
            <div key={e.id} className="bg-gray-800 border border-gray-700 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <span className="text-xs px-2 py-0.5 rounded-full bg-orange-900/40 text-orange-400">{e.exposure_type}</span>
                <span className="text-white font-semibold text-sm">{e.description}</span>
                {e.amount && <span className="ml-auto text-yellow-400 font-bold">${e.amount?.toLocaleString()}</span>}
              </div>
            </div>
          ))}
          {!exposures?.length && <div className="text-gray-500 text-center py-8">No open exposures</div>}
        </div>
      )}
    </div>
  );
}
